import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { injectIntl } from "@edx/frontend-platform/i18n";
import { Button, Spinner, Stack } from "@openedx/paragon";
import { useDispatch, useSelector } from "react-redux";

import { fetchBlockElectives, saveBlockElectives } from "../../../data";
import { setElectiveAfterSubmitModalOpen, setTotalSelectedElectives } from "../../../data/slice";
import CardItem from "./CardItem";
import { useModel } from "../../../../generic/model-store";
import { electivesStatusSelector, selectElectiveAfterSubmitModalOpen, selectTotalSelectedElectives } from "../../../../courseware/data/selectors";
import BookmarkButton from "../../bookmark/BookmarkButton";
import messages from "../messages";
import PageLoading from "../../../../generic/PageLoading";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import ElectiveDetailsModal from "./ElectiveDetailsModal";
import ConfirmationModal from "../../../../generic/confirmationModal";
import { getLocalizedFormattedNumber } from "../../../../utils/i18n";
import TextBlockSkeleton from "../../../../components/Skeletons/TextBlockSkeleton";
import ElectiveSubmitModal from "./ElectiveSubmitModal";

const defaultMinReqElectives = 1;

function ElectiveCourses({ sectionId, intl, unitId, courseId, sequenceId }) {
  const [isOpen, setIsOpen] = useState(false);
  const [electiveInfo, setElectiveInfo] = useState({});
  const [selectedElectives, setSelectedElectives] = useState({});
  const [isHover, setIsHover] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [electiveAriaMsg, setElectiveAriaMsg] = useState('');
  const statusMsgRef = useRef(null);
  const dispatch = useDispatch();

  const isElectiveAfterSubmitModalOpen = useSelector(selectElectiveAfterSubmitModalOpen);
  const closeAfterSubmitModal = useCallback(() => { dispatch(setElectiveAfterSubmitModalOpen(false)); }, []);
  const openAfterSubmitModal = useCallback(() => { dispatch(setElectiveAfterSubmitModalOpen(true)); }, []);
  
  const totalSelectedElectives = useSelector(selectTotalSelectedElectives);

  const { minReqElectives, electives=[] } = useModel("electives", sectionId);
  const totalElectives = electives?.length;
  const unit = useModel("units", unitId);

  const prevTotalSelectedElectives = useMemo(() => {
    return electives.reduce((acc, { isSelected }) => {
      return isSelected ? acc + 1 : acc;
    }, 0);
  }, [electives]);

  const { isElectivesUpdating, isElectivesLoading } = useSelector(
    electivesStatusSelector
  );
  const { locale = 'en' } = useSelector((state) => state.app);

  const totalCompletedElectives = useMemo(() => {
    return electives.reduce((acc, { isComplete }) => {
      return isComplete ? acc + 1 : acc;
    } , 0);
  }, [electives]);

  const prevElectiveCount = electives?.reduce(
    (acc, { isComplete, isSelected, subsection }) => {
      if (
        isComplete ||
        (isSelected && selectedElectives?.[subsection] === undefined)
      ) {
        return acc + 1;
      }

      return acc;
    },
    0
  );

  const updatedElectiveCount =
    Object.keys(selectedElectives).reduce((acc, key) => {
      return selectedElectives[key] ? acc + 1 : acc;
    }, 0) || 0;

  const isDisabled =
    prevElectiveCount + updatedElectiveCount < defaultMinReqElectives ||
    !Object.keys(selectedElectives).length;

  const isElectiveCompleted = electiveInfo.isComplete;

  const getIsElectiveSelected = (elective) => {
    return selectedElectives[elective.subsection] !== undefined
      ? selectedElectives[elective.subsection]
      : elective.isSelected;
  };

  const handleUpdateElective = (event, elective) => {
    const { isSelected } = elective;
    const { name, checked } = event.target;

    if ((isSelected && checked) || (!isSelected && !checked)) {
      const filteredElectives = Object.keys(selectedElectives).reduce(
        (acc, key) => {
          if (key === name) {
            return acc;
          }

          return { ...acc, [key]: selectedElectives[key] };
        },
        {}
      );
      setSelectedElectives(filteredElectives);
    } else {
      setSelectedElectives((prev) => ({ ...prev, [name]: checked }));
    }
  };

  const handleOpenModal = (elective) => {
    setElectiveInfo(elective);
    setIsOpen(true);
  };

  const handleCloseModal = () => {
    setElectiveInfo({});
    setIsOpen(false);
  };

  useEffect(() => {
    dispatch(fetchBlockElectives(sectionId));
  }, []);

  useEffect(() => {
    setElectiveAriaMsg(() => 
      isElectivesUpdating 
        ? intl.formatMessage(messages.electiveAriaSubmit) 
        : intl.formatMessage(messages.electiveAriaSave)
    );

    if (statusMsgRef.current) {
      statusMsgRef.current.focus(); // Example: set focus to the element
    }

    const timeoutId = setTimeout(() => {
      setElectiveAriaMsg('');
    }, 5000);
  
    return () => clearTimeout(timeoutId);
  }, [isElectivesUpdating]);

  const handleConfirmation = useCallback(async () => {
    const dataToUpdate = Object.keys(selectedElectives).reduce((acc, key) => {
      return { ...acc, [key]: { is_selected: selectedElectives[key] } };
    }, {});

    const updatedTotalSelectedElectives = Object.keys(dataToUpdate).reduce((acc, key) => {
      return dataToUpdate[key].is_selected ? acc + 1 : acc - 1;
    }, prevTotalSelectedElectives);
    dispatch(setTotalSelectedElectives(updatedTotalSelectedElectives));
    setIsConfirmModalOpen(false);
    try {
      await dispatch(
        saveBlockElectives({
          sectionId,
          dataToUpdate,
          courseId,
          sequenceId,
        })
      );
      setSelectedElectives({});

      if (minReqElectives > 0) {
        openAfterSubmitModal();
      }
    } catch {}
  }, [selectedElectives, sectionId, courseId]);

  const handleSubmitForm = useCallback(() => {
    const hasUnselectInprogressElective = electives.some(({ subsection }) => {
      return selectedElectives[subsection] === false;
    });

    if (hasUnselectInprogressElective) {
      setIsConfirmModalOpen(true);
      return;
    }

    handleConfirmation();
  }, [electives, selectedElectives]);

  const isMultipleProjects = minReqElectives > 1 ? 's' : '';

  return (
    <div className="unit">
      <div className="adjust-padding1">
        <h1 className="mb-0 h3 fw-500">{unit.title}</h1>
        <h2 className="sr-only">
          {intl.formatMessage(messages.headerPlaceholder)}
        </h2>
        <BookmarkButton
          unitId={unit.id}
          isBookmarked={unit.bookmarked}
          isProcessing={unit.bookmarkedUpdateState === 'loading'}
        />
      </div>
      <div className="elective-container">
        {isElectivesLoading ? (
          <div className="d-flex flex-column justify-content-center align-items-center flex-grow-1">
            {[1, 2].map((i) => (
              <TextBlockSkeleton
                key={i}
                srMessage={
                  i === 1 ? intl.formatMessage(messages.loadingElectives) : ''
                }
              />
            ))}
          </div>
        ) : (
          <>
            <div>
              <h2 className="elective-title">
                {intl.formatMessage(messages.electiveTitle)}
              </h2>
              <div className="elective-description">
                {minReqElectives
                  ? intl.formatMessage(
                      messages.electiveDescriptionMinReqElective,
                      {
                        minReqElectivesNumber: minReqElectives,
                        minReqElectives: getLocalizedFormattedNumber(
                          minReqElectives,
                          locale,
                          true
                        ),
                        isMultipleProjects,
                      }
                    )
                  : intl.formatMessage(
                      messages.electiveDescriptionNoMinReqElective
                    )}
              </div>
            </div>
            <div>
              {minReqElectives > 0 ? (
                <h3 className="elective-title text-center mb-4">
                  {intl.formatMessage(messages.electiveMinReqTitle, {
                    minReqElectivesNumber: minReqElectives,
                    minReqElectives:
                      getLocalizedFormattedNumber(minReqElectives),
                  })}
                </h3>
              ) : null}
              <div className="elective-cards">
                {electives?.map((elective) => (
                  <CardItem
                    elective={elective}
                    handleOpenModal={handleOpenModal}
                    handleUpdateElective={handleUpdateElective}
                    selectedElectives={selectedElectives}
                  />
                ))}
              </div>
            </div>

            {minReqElectives > 0 ? (
              <Stack
                className="mt-5 justify-content-center align-items-center"
                gap={3}
                direction="horizontal"
              >
                {totalCompletedElectives >= minReqElectives ? (
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    className="text-success"
                    aria-hidden="true"
                  />
                ) : null}
                <h3 className="elective-title mb-0">
                  {intl.formatMessage(
                    messages.electiveMinReqElectivesCompleted,
                    { totalCompletedElectives, minReqElectives }
                  )}
                </h3>
              </Stack>
            ) : (
              <h3 className="elective-title mt-5 text-center">
                {intl.formatMessage(
                  messages.electiveNoMinReqElectivesCompleted,
                  { totalCompletedElectives }
                )}
              </h3>
            )}

            <div className="form-button">
              <span
                id="submitBtn"
                onMouseEnter={() => setIsHover(true)}
                onMouseLeave={() => setIsHover(false)}
              >
                <Button
                  variant="outline-primary"
                  onClick={() => handleSubmitForm()}
                  disabled={isDisabled}
                >
                  {isElectivesUpdating ? (
                    <Spinner animation="border" />
                  ) : (
                    intl.formatMessage(messages.electiveFormSubmitText)
                  )}
                </Button>
              </span>
              <div
                ref={statusMsgRef}
                className="message sr-only"
                aria-live="assertive"
                role="alert"
              >
                {electiveAriaMsg}
              </div>
              <ReactTooltip
                isOpen={isDisabled && isHover}
                anchorId="submitBtn"
                place="right"
                children={
                  <div>
                    {intl.formatMessage(messages.electiveFormSubmitToolTip)}
                  </div>
                }
              />
            </div>
          </>
        )}
      </div>
      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        handleClose={() => setIsConfirmModalOpen(false)}
        handleConfirmation={handleConfirmation}
      >
        {intl.formatMessage(messages.electiveFormSubmitConfirmationText)}
      </ConfirmationModal>
      <ElectiveDetailsModal
        electiveInfo={electiveInfo}
        isOpen={isOpen}
        handleCloseModal={handleCloseModal}
        handleUpdateElective={handleUpdateElective}
        isElectiveCompleted={isElectiveCompleted}
        getIsElectiveSelected={getIsElectiveSelected}
      />

      <ElectiveSubmitModal
        isOpen={isElectiveAfterSubmitModalOpen}
        handleCloseModal={closeAfterSubmitModal}
        minReqElectives={minReqElectives}
        totalSelectedElectives={totalSelectedElectives}
      />
    </div>
  );
}

export default injectIntl(ElectiveCourses);
